import { useAtom } from "jotai";
import { hcostInboundAtom, hcostInboundBrokerageValueAtom } from "@/store/hcost-atom";
import React, { useEffect, useMemo, useRef } from "react";
import { cloneBrokerage } from "@service/hcost/hcost-result-service";
import { commonNotify } from "@utils/common";
import { Column, DataGrid, Editing, Item, Toolbar } from "devextreme-react/data-grid";
import { BrokerageEditComponent } from "@feature/brokerage/counsel-brokerage-edit";
import { BrokerageIdComponent } from "@feature/brokerage/counsel-brokerage-cell";
import { cloneDeep } from "lodash-es";
import useCounselHook from "@feature/counsel/useCounselHook";
import { CheckBox } from "devextreme-react";
import { CellPreparedEvent } from "devextreme/ui/data_grid";
import GridButton from "@feature/brokerage/brokerage-grid/grid-button";
interface Props {
  accessType: string;
}

const CounselBrokerage = ({ accessType }: Props) => {
  const [hCostResult, setHCostResult] = useAtom(hcostInboundAtom);
  const [brokerages, setBrokerages] = useAtom(hcostInboundBrokerageValueAtom);
  const dataGridRef = useRef<DataGrid>();

  const { getHCostCounsel, onValueChangeCounselHandler } = useCounselHook();

  const addRowWidgetOptions = {
    icon: "plus",
    onClick: () => {
      dataGridRef.current.instance.addRow().catch(e => console.log(e));
    },
  };

  const allowBrokerageHandler = (e: any, data: any) => {
    if (data.data && data.data.id && data.data.company) {
      cloneBrokerage(accessType, hCostResult.id, data.data.company.id).then(() => {
        commonNotify(`${data.data.company.companyName}으로 상담건이 전달되었습니다.`);
        void getHCostCounsel(hCostResult.id, accessType, 0);
      });
    }
  };

  useEffect(() => {
    setBrokerages(cloneDeep(hCostResult.counselBrokerages));
  }, [hCostResult.counselBrokerages, setBrokerages]);

  const beforeSave = (e: any) => {
    const result = cloneDeep(brokerages);
    if (e.changes.length && (e.changes[0].type === "insert" || e.changes[0].type === "update")) {
      const changeData = e.changes[0].data;

      const isExist = brokerages?.find(v => v.company.id === changeData.company.id);
      if (isExist) {
        e.cancel = true;
        commonNotify("이미 중개된 조직입니다.", "error");
      } else {
        if (e.changes[0].type === "update") {
          // const updateData = result.findIndex(v => v.id === e.changes[0].key.id);
          // result.splice(updateData, 1, e.changes[0].key);
          // setBrokerages(result);
        } else {
          result.push(changeData);
          setBrokerages(result);
        }
      }
    } else if (e.changes.length && e.changes[0].type === "remove") {
      result.splice(
        result.findIndex((v: any) => v?.company?.id === e.changes[0].key?.company?.id),
        1,
      );
      setBrokerages(result);
      if (!result.length) {
        setHCostResult(prev => {
          prev.conBrokerage = false;
        });
      }
    }
  };

  function setStateValue(rowData: CounselBrokerage, value: any) {
    if (value) {
      rowData.company = { id: null, companyName: null, ceoName: null, address: null };
      rowData.counsel = { id: null, counselor: null, progressStatus: null, createDate: null };
      rowData.company.id = value.id;
      rowData.company.companyName = value.companyName;
      rowData.company.ceoName = value.ceoName;
      rowData.company.address = value.address;
    }
  }

  const conBrokerage = useMemo(
    () => (
      <CheckBox
        value={hCostResult.conBrokerage}
        readOnly={!brokerages.length}
        onValueChanged={onValueChangeCounselHandler((draft, value) => {
          draft.conBrokerage = value;
        })}
      />
    ),
    [brokerages.length, hCostResult.conBrokerage, onValueChangeCounselHandler],
  );
  const onCellPrepared = (e: CellPreparedEvent) => {
    if (e.rowType === "data" && e.column.type === "buttons") {
      if (e.data.counsel && e.data.counsel.id) {
        Object.assign(e.cellElement.style, { visibility: "hidden" });
      }
    }
  };

  return (
    <div className="content-fieldset">
      <h3 className="content-fieldset-title full flex justify-between">중개 정보</h3>
      <div className="content-fieldset-title-desc" />

      <div className="content-field row ">
        <div className="content-field-label">
          <div className="content-label-name">공개 여부</div>
          <div className="content-label-desc" />
        </div>
        <div className="content-field-value full">
          {conBrokerage}
          <div className="content-value-desc"></div>
        </div>
      </div>

      <div className="border-[#f2f2f2] w-full p-4 border-b">
        <div className="content-field-value full flex flex-wrap">
          <DataGrid
            dataSource={brokerages}
            onSaving={beforeSave}
            ref={dataGridRef}
            showBorders={false}
            allowColumnResizing
            showColumnLines
            hoverStateEnabled
            onCellPrepared={onCellPrepared}
          >
            <Editing mode="row" allowAdding={true} allowDeleting={true} useIcons={true} allowUpdating={true} />

            <Column
              caption="조직 ID"
              alignment="center"
              dataField="company.id"
              dataType="number"
              width="60"
              allowEditing={false}
            />
            <Column
              alignment="left"
              caption="조직명"
              dataField="company.companyName"
              editCellComponent={e => BrokerageEditComponent(e, dataGridRef)}
              setCellValue={setStateValue}
              dataType="string"
              name="brokerages"
              width="120"
            />
            <Column
              alignment="left"
              caption="주소"
              dataField="company.address.road"
              dataType="string"
              allowEditing={false}
            />
            <Column
              caption="상담 ID"
              alignment="center"
              dataField="counsel.id"
              cellComponent={e =>
                e.data.data.counsel && e.data.data.counsel.id
                  ? BrokerageIdComponent(e)
                  : GridButton(e, allowBrokerageHandler, "중개수락", "text-xs")
              }
              dataType="number"
              width="70"
              allowEditing={false}
            />
            <Column
              caption="담당자"
              dataField="counsel.counselor.name"
              dataType="string"
              width="80"
              allowEditing={false}
            />
            <Column
              caption="진행상태"
              dataField="counsel.progressStatus.value"
              dataType="string"
              width="70"
              allowEditing={false}
            />
            <Column
              caption="중개일시"
              dataField="counsel.createDate"
              dataType="date"
              format="yyyy-MM-dd"
              width="90"
              allowEditing={false}
            />
            <Toolbar>
              <Item text="중개 조직" cssClass="!text-sm !px-9" location="before" />
              <Item widget="dxButton" options={addRowWidgetOptions} location="after" />
            </Toolbar>
          </DataGrid>
          <div className="content-value-desc" />
        </div>
      </div>
    </div>
  );
};

export default CounselBrokerage;
